import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  CheckSquare,
  Eye,
  FileText,
  Check,
  X,
  MessageSquare,
  Loader2,
  Facebook,
  Mail,
  Calendar,
  User,
  Target
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  approved: 'bg-green-500/20 text-green-400 border-green-500/30',
  changes_requested: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
};

const PLATFORM_ICONS = {
  facebook: Facebook,
  email: Mail
};

function ApprovalQueue() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns/queue`);
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Load campaigns error:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (campaignId, status, notes = '') => {
    try {
      await axios.put(`${API}/campaigns/${campaignId}/status`, { status, notes });
      toast.success(`Campaign ${status.replace('_', ' ')}`);
      loadCampaigns();
    } catch (error) {
      console.error('Update status error:', error);
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Approval Queue</h1>
        <p className="text-white/60">Review and approve pending campaigns</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card className="card">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-yellow-400">{campaigns.filter(c => c.status === 'pending').length}</p>
            <p className="text-sm text-white/60">Pending</p>
          </CardContent>
        </Card>
        <Card className="card">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-400">{campaigns.filter(c => c.status === 'approved').length}</p>
            <p className="text-sm text-white/60">Approved</p>
          </CardContent>
        </Card>
        <Card className="card">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-orange-400">{campaigns.filter(c => c.status === 'changes_requested').length}</p>
            <p className="text-sm text-white/60">Changes Requested</p>
          </CardContent>
        </Card>
        <Card className="card">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-white">{campaigns.length}</p>
            <p className="text-sm text-white/60">Total</p>
          </CardContent>
        </Card>
      </div>

      {/* Campaign List */}
      {campaigns.length === 0 ? (
        <Card className="card">
          <CardContent className="p-12 text-center">
            <CheckSquare className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Pending Campaigns</h3>
            <p className="text-white/60 mb-4">All campaigns have been reviewed</p>
            <Button
              onClick={() => navigate('/campaigns/create')}
              className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black"
            >
              Create New Campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {campaigns.map((campaign, idx) => {
            const PlatformIcon = PLATFORM_ICONS[campaign.platform] || FileText;
            
            return (
              <Card key={idx} className="card hover:border-white/20 transition-all">
                <CardContent className="p-4 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* Left: Info */}
                    <div className="flex items-start gap-4 flex-1">
                      <div className="p-3 bg-[#00d4aa]/20 rounded-lg">
                        <PlatformIcon className="w-6 h-6 text-[#00d4aa]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-semibold text-white truncate">
                            {campaign.campaignName}
                          </h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[campaign.status]}`}>
                            {campaign.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/60">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {campaign.submittedBy}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(campaign.submittedAt || campaign.createdAt).toLocaleDateString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Target className="w-3 h-3" />
                            {campaign.clientName}
                          </span>
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {campaign.ads?.length || 0} ads
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/campaigns/${campaign.campaign_id || idx}`)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => updateStatus(campaign.campaign_id || idx, 'approved')}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const notes = prompt('Enter feedback for changes:');
                          if (notes) updateStatus(campaign.campaign_id || idx, 'changes_requested', notes);
                        }}
                        className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
                      >
                        <MessageSquare className="w-4 h-4 mr-1" />
                        Changes
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateStatus(campaign.campaign_id || idx, 'rejected')}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ApprovalQueue;
